import Cookies from 'js-cookie';
import { isPublicPath } from '@/../../shared/utils/auth';
import { isSessionAboutToExpire, shouldSkipRedirectionOnAuthError } from '@/utils/auth';

const REFRESH_TOKEN_URL = '/api/auth/refresh-token';

export const refreshToken = async (): Promise<string> => {
  try {
    const response = await fetch(REFRESH_TOKEN_URL, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}), // Send empty object to satisfy JSON parser
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const data = await response.json();
    return data.sessionToken;
  } catch (error) {
    console.error('Error refreshing token:', error);
    throw error;
  }
};

export const setAuthToken = (token: string) => {
  localStorage.setItem('sessionToken', token);
};

export const clearAuthToken = () => {
  localStorage.removeItem('sessionToken');
  Cookies.remove('refreshToken');
};

export const getAuthToken = (): string | null => {
  return localStorage.getItem('sessionToken');
};

// Shared promise to prevent multiple simultaneous refresh attempts
let refreshTokenPromise: Promise<string> | null = null;

export const ensureValidToken = async (url: string): Promise<string | null> => {
  // Skip token refresh for public paths
  if (isPublicPath(url)) {
    return getAuthToken();
  }

  // Wait for any ongoing refresh to complete
  if (refreshTokenPromise) {
    console.log('Request deferred because token refreshing is in progress');
    await refreshTokenPromise;
  }

  const token = getAuthToken();

  // Only refresh token if it's about to expire
  if (token && isSessionAboutToExpire(token)) {
    refreshTokenPromise = refreshToken();
    try {
      const newToken = await refreshTokenPromise;
      setAuthToken(newToken);
      console.log('Refreshed token:', newToken);
      return newToken;
    } catch (error) {
      clearAuthToken();
      throw error;
    } finally {
      refreshTokenPromise = null;
    }
  }

  return token;
};

// Helper function to create fetch with auth
export const fetchWithAuth = async (url: string, options: RequestInit = {}): Promise<Response> => {
  try {
    const token = await ensureValidToken(url);

    const headers = new Headers(options.headers);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    // Handle 401 errors
    if (response.status === 401 && !isPublicPath(url)) {
      const responseData = await response.json().catch(() => ({}));
      const hasRefreshToken = responseData.hasRefreshToken;

      if (hasRefreshToken) {
        // Try to refresh token and retry the request
        try {
          const newToken = await refreshToken();
          setAuthToken(newToken);

          headers.set('Authorization', `Bearer ${newToken}`);
          return fetch(url, {
            ...options,
            headers,
            credentials: 'include',
          });
        } catch (refreshError) {
          clearAuthToken();
          // Redirect to signup if not on a whitelisted path
          if (!shouldSkipRedirectionOnAuthError(window.location.pathname)) {
            window.location.href = '/signup';
          }
          throw refreshError;
        }
      }
    }

    return response;
  } catch (error) {
    console.error('Fetch with auth error:', error);
    throw error;
  }
};
